import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Mail } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ChatThread } from "@/components/app/ChatThread";

export const AdminMessages = ({ selected, setSelected }) => {
  const [threads, setThreads] = useState([]);
  useEffect(() => {
    const f = () => api.get("/admin/threads").then((r) => setThreads(r.data)).catch(() => {});
    f();
    const t = setInterval(f, 5000);
    return () => clearInterval(t);
  }, []);
  const uid = selected?.id;
  const load = useCallback(() => api.get(`/admin/threads/${uid}/messages`).then((r) => r.data), [uid]);
  const sendFn = useCallback((text) => api.post(`/admin/threads/${uid}/messages`, { text }).then((r) => r.data), [uid]);
  const list = selected && !threads.some((t) => t.user_id === selected.id) ? [{ user_id: selected.id, user: selected, last_message: "Nuova conversazione" }, ...threads] : threads;

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[70vh]" data-testid="admin-messages">
      <div className="hud-card p-3 overflow-y-auto">
        <p className="hud-tag mb-2 px-1">Conversazioni</p>
        {list.length === 0 && <p className="text-sm text-slate-500 p-2">Nessun messaggio.</p>}
        {list.map((t) => (
          <button key={t.user_id} onClick={() => setSelected({ id: t.user_id, ...(t.user || {}) })} data-testid={`thread-item-${t.user_id}`}
            className={`w-full text-left rounded-lg px-3 py-2.5 mb-1 transition-colors ${uid === t.user_id ? "bg-cyan-500/15 border border-cyan-400/30" : "hover:bg-white/5 border border-transparent"}`}>
            <div className="flex items-center gap-2"><span className="text-sm text-white truncate flex-1">{t.user?.name || "Utente"}</span>{t.unread_admin > 0 && <span className="text-[10px] bg-amber-400 text-black rounded-full px-1.5">{t.unread_admin}</span>}</div>
            <p className="text-xs text-slate-500 truncate">{t.last_message}</p>
          </button>
        ))}
      </div>
      <div className="hud-card flex flex-col overflow-hidden">
        {uid ? (
          <>
            <div className="px-4 py-3 border-b border-cyan-500/15"><p className="text-white font-medium">{selected.name}</p><p className="text-xs text-slate-500">{selected.email}</p></div>
            <ChatThread key={uid} load={load} sendFn={sendFn} mine="admin" emptyText="Inizia la conversazione con questo utente." testPrefix="admin-chat" />
          </>
        ) : <div className="flex-1 flex items-center justify-center text-sm text-slate-500">Seleziona una conversazione</div>}
      </div>
    </div>
  );
};

export const AdminPush = ({ target, setTarget, users }) => {
  const [f, setF] = useState({ title: "", body: "" });
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/push").then((r) => setHist(r.data));
  useEffect(() => { load(); }, []);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push", { ...f, user_id: target?.id || null });
      toast.success(`Notifica inviata a ${data.sent} dispositivo/i`);
      setF({ title: "", body: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const reminders = async () => {
    try { const { data } = await api.post("/admin/renewal-reminders"); toast.success(`Promemoria inviati: ${data.sent}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "w-full bg-[#050b18]/80 border border-cyan-500/25 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-cyan-400/60";
  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="admin-push">
      <form onSubmit={send} className="hud-card p-5 space-y-3">
        <p className="hud-tag">Nuova notifica push</p>
        <select className={inp} value={target?.id || ""} onChange={(e) => setTarget(users.find((u) => u.id === e.target.value) || null)} data-testid="push-target-select">
          <option value="">Tutti gli utenti</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
        </select>
        <input className={inp} placeholder="Titolo" required maxLength={80} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="push-title-input" />
        <textarea className={inp} placeholder="Messaggio" required rows={3} maxLength={300} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="push-body-input" />
        <button disabled={busy} className="btn-aurora rounded-xl px-6 py-2.5 text-sm uppercase flex items-center gap-2" data-testid="admin-send-push-btn"><Send size={15} /> Invia notifica</button>
        <p className="text-xs text-slate-500">Arriva solo agli utenti che hanno attivato le notifiche su un dispositivo.</p>
        <div className="pt-3 border-t border-cyan-500/10">
          <button type="button" onClick={reminders} className="btn-ghost-hud rounded-xl px-4 py-2 text-xs flex items-center gap-2" data-testid="btn-renewal-reminders"><Mail size={14} /> Invia ora promemoria rinnovo (scadenza ≤ 3 giorni)</button>
        </div>
      </form>
      <div className="hud-card p-5">
        <p className="hud-tag mb-3">Storico notifiche</p>
        <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="push-history">
          {hist.length === 0 && <p className="text-sm text-slate-500">Nessuna notifica inviata.</p>}
          {hist.map((h) => (
            <div key={h.id} className="rounded-lg border border-cyan-500/15 p-3">
              <p className="text-sm text-white">{h.title}</p><p className="text-xs text-slate-400">{h.body}</p>
              <p className="text-[10px] font-mono text-slate-500 mt-1">{new Date(h.created_at).toLocaleString("it-IT")} · {h.user_id ? "singolo" : "tutti"} · consegnate {h.sent}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
